import { Collection, CollectionTarget } from "@ciaopeople/cp-shatsu/DomHelper";
import { EventHandler, SimpleEventHandler } from "@ciaopeople/cp-shatsu/EventHandler";
import { flatten, hasOwnProperty } from "@ciaopeople/cp-shatsu/Utils";
import { GroupableData } from "../../api/commondata/GroupableData.js";
import { UserActionError, UserActionErrorCode, UserActionIdentifier } from "../../api/commondata/UserActionError.js";
import { RESTAPIEventNames, RESTAPIEvents } from "../../api/RESTAPIEvents.js";
import { RESTUserServices } from "../../api/RESTUserServices.js";
import { BookmarkQueryObj, BookmarkQueryWithSaved, isBookmarkQueryByExternalId, isBookmarkQueryById, isBookmarkQueryByUrl, isBookmarkQueryObj, isBookmarkQueryWithSaved } from "../../api/types/BookmarkingDataTypes.js";
import { AAABarrierWithSource } from "../aaa/AAABarrierWithSource.js";
import { ActionableEvents, AttachAndStatusData, ElementWithAttachData, ParseAndAttachModule } from "./ParseAndAttachModule.js";
import { SdkHandledHTMLElement } from "./SdkHandledHTMLElement.js";
import { UserActionExtraInfo } from "../../api/types/NotificationDataTypes.js";

type Action = (evt: string, data?: any) => void;

class BookmarkHTMLElement extends SdkHandledHTMLElement<boolean> {

    public setStatus(s: boolean): SdkHandledHTMLElement<boolean> {
        this.attr("data-saved", s?"true":"false");
        return this;
    }
    public getStatus(): boolean {
        return this.firstAttr("data-saved") == "true";
    }

    public getUrl(): string {
        return this.firstAttr("data-url");
    }
    public getUTMSource(): string {
        const incomingSource = super.getUTMSource();
        return incomingSource && incomingSource != ""?incomingSource:"bookmark";
    }
    public getAssociatedUserAction(): UserActionIdentifier {
        return UserActionIdentifier.BOOKMARK;
    }
 

}


class GroupableBookmarkQueryObj implements GroupableData {
    

    constructor(public readonly query: BookmarkQueryObj) {

    }

    getGroupName(): string {
        if(isBookmarkQueryByUrl(this.query)) {
            return `[url]-${this.query.url}`;
        }
        else if(isBookmarkQueryByExternalId(this.query)) {
            return `[extid]-${this.query.ext_id}`;
        }
        else if(isBookmarkQueryById(this.query)) {
            return `[sid]-${this.query.id}`;
        }
        else return null;
    }

}

export interface BookmarkEventWithInvolvedDOM extends BookmarkQueryWithSaved {
    dom: HTMLElement[]
}

export function isBookmarkEventWithInvolvedDOM(obj: any): obj is BookmarkEventWithInvolvedDOM {
    return isBookmarkQueryWithSaved(obj) && hasOwnProperty(obj, "dom");
}

export class BookmarkParseAndAttachModule extends ParseAndAttachModule<boolean, GroupableBookmarkQueryObj> implements EventHandler {


    private eh: EventHandler;


    constructor(private restServices: AAABarrierWithSource<SdkHandledHTMLElement<any>, RESTUserServices>, 
                private restEvents: RESTAPIEvents) {
        super();
        this.eh = new SimpleEventHandler();
        this.restEvents.onArticleSaveStatusChange((bookmarkData) => {
            if(isBookmarkQueryByUrl(bookmarkData.query)) {
                this.updatedElementStatuses(
                    [{
                        attachData: new GroupableBookmarkQueryObj(bookmarkData.query),
                        status: bookmarkData.saved
                    }]                
                );
            }   
        });
    }

    public parseAndAttach(el: CollectionTarget | CollectionTarget[], _trigger?: never): Promise<void> {
        return super.parseAndAttach(el, ActionableEvents.click);
    }

    protected extractAndCreate(el: HTMLElement): ElementWithAttachData<boolean, GroupableBookmarkQueryObj> {
        const parsed = new BookmarkHTMLElement(el);
        if(parsed.getUrl() != "") {
            return {
                element: parsed,
                attachData: new GroupableBookmarkQueryObj({
                    url: parsed.getUrl()
                })
            };
        }
        else return null;
    }

    public action(el: ElementWithAttachData<boolean, GroupableBookmarkQueryObj>): void {
        this.restServices.executeWithBarrierWithSource(el.element, service => {
            const bookmarkElement = el.element;
            const bookmarkElementStatus = bookmarkElement.getStatus();
            const umed = bookmarkElement.getUTMmedium();
            const maybeExtraInfo: UserActionExtraInfo = umed && umed != ""?{source_element: umed}:null;
            const actionPromise = bookmarkElementStatus?
                service.unbookmark(el.attachData.query, maybeExtraInfo):
                service.bookmark(el.attachData.query, maybeExtraInfo);
          
            return actionPromise.then(resp =>  {
                el.element.setStatus(!bookmarkElementStatus);
                this.success.next({
                    ...el,
                    action: UserActionIdentifier.BOOKMARK
                });
                return resp;
            }).catch(err => {
                const uaErr: UserActionError = {
                    code: UserActionErrorCode.UNKNOWN_ERROR,
                    action: UserActionIdentifier.BOOKMARK,
                    message: err,
                    data: {
                        query: el.attachData
                    }
                };
                this.errors.next(uaErr);
                return Promise.reject(uaErr);
            });
        });
        
    }

    protected afterAttach(newlyAttached: ElementWithAttachData<boolean, GroupableBookmarkQueryObj>[]): Promise<void> {
        if(newlyAttached && newlyAttached.length > 0) {
            const checkReq = {
                queries: newlyAttached.map(el => el.attachData.query)
            };
            return this.restServices.getBarricadedService().checkSavedArticles(checkReq).then();
        }
        else return Promise.resolve();
    }

    protected afterUpdate(s: AttachAndStatusData<boolean, GroupableBookmarkQueryObj>, involvedElements: SdkHandledHTMLElement<boolean>[]): void {
        
        this.triggerArticleSaveStatusChange(         
            {
                dom: flatten(involvedElements.map(ie => ie.ig())),
                query: s.attachData.query,
                saved: s.status
            }
        );

    }


    private triggerArticleSaveStatusChange(evt: BookmarkEventWithInvolvedDOM): void {
        this.trigger(RESTAPIEventNames.ARTICLE_SAVE_STATUS_CHANGE, evt);
    }

    public onArticleSaveStatusChange(fun: (evt: BookmarkEventWithInvolvedDOM) => any): void {
        this.on(RESTAPIEventNames.ARTICLE_SAVE_STATUS_CHANGE, (evt, data) => {
            if(isBookmarkEventWithInvolvedDOM(data)) fun(data);
        });
    }

    on(eventName: string, callback: (evt: string, data?: any) => void) {
        this.eh.on(eventName, callback);
    }
    off(act: Action);
    off(evt: string, act: Action);
    off(evtOrAct: string | Action, act?: Action);
    off(evtOrAct: string | Action, act?: Action): any {
        return this.eh.off(evtOrAct, act);    
    }
    trigger(eventName: string, data: any): void {
        this.eh.trigger(eventName, data);
    }
    one(eventName: string, callback: Action): void {
        this.eh.one(eventName, callback);
    }

}